import { useRef, useState } from "react";
import { FileSpreadsheet, Download, Upload, Loader2, AlertTriangle, CheckCircle2, Mail, Users } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { authApi, formatApiError } from "@/lib/api";
import { readFileAsDataUrl } from "@/lib/files";

export default function CargaMassiva() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null); // { name, dataUrl }
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await authApi().get("/users/carga-massiva/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template_carga_massiva_bonsai.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const pickFile = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Envie a planilha .xlsx gerada a partir do template.");
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(f, 6);
      setFile({ name: f.name, dataUrl });
      setPreview(null);
      setResult(null);
    } catch (err) {
      toast.error(formatApiError(err.message));
    }
  };

  const validar = async () => {
    setBusy(true);
    try {
      const r = await authApi().post("/users/carga-massiva", {
        file_name: file.name,
        data_base64: file.dataUrl.split(";base64,")[1],
        confirm: false,
      });
      setPreview(r.data);
      if (r.data.validos === 0) toast.error("Nenhuma linha válida para importar. Corrija os erros na planilha.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const confirmar = async () => {
    setBusy(true);
    try {
      const r = await authApi().post("/users/carga-massiva", {
        file_name: file.name,
        data_base64: file.dataUrl.split(";base64,")[1],
        confirm: true,
      });
      setResult(r.data);
      setPreview(null);
      toast.success(`${r.data.criados} usuário(s) importado(s).`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const downloadErros = (erros) => {
    const linhas = ["linha;nome;email;problemas"];
    erros.forEach((e) => linhas.push(`${e.linha};${e.nome};${e.email};${e.erros.join(" | ")}`));
    const blob = new Blob(["\uFEFF" + linhas.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "erros_carga_massiva.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const ErrosTable = ({ erros, testid }) => (
    <div className="mt-4 rounded-xl border border-crimson/40 bg-crimson/[0.04] p-4" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="font-mono text-[10px] tracking-widest text-crimson flex items-center gap-2">
          <AlertTriangle size={13} /> {erros.length} LINHA(S) COM ERRO — CORRIJA NA PLANILHA E REENVIE
        </p>
        <button type="button" onClick={() => downloadErros(erros)} data-testid={`${testid}-download`}
          className="font-mono text-[10px] tracking-widest px-3 py-1.5 rounded-full border border-crimson/50 text-crimson hover:bg-crimson/15 transition-colors inline-flex items-center gap-1.5">
          <Download size={11} /> BAIXAR LISTA (CSV)
        </button>
      </div>
      <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
        {erros.map((e, i) => (
          <div key={i} className="text-xs border-b border-white/5 pb-2" data-testid={`${testid}-row-${i}`}>
            <p className="text-zinc-300"><span className="font-mono text-zinc-500">L{e.linha}</span> · {e.nome} · {e.email}</p>
            <ul className="mt-1 ml-4 list-disc text-crimson/90">
              {e.erros.map((p, j) => <li key={j}>{p}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <AppShell active="usuarios" requireRoles={["admin", "responsavel"]}>
      <div className="relative" data-testid="carga-massiva-page">
        <div className="absolute inset-0 flex items-start justify-center pt-24 pointer-events-none overflow-hidden">
          <img src="/assets/logo.jpeg" alt="" className="w-[45%] max-w-md opacity-[0.05] rounded-full" />
        </div>

        <div className="relative max-w-3xl">
          <p className="font-mono text-[10px] tracking-[0.3em] text-gold">USUÁRIOS</p>
          <h1 className="mt-1 font-display text-2xl font-bold flex items-center gap-3" data-testid="carga-title">
            <FileSpreadsheet size={22} className="text-gold" /> Carga Massiva
          </h1>
          <p className="mt-1 text-sm text-zinc-400">Importe vários usuários de uma vez com a planilha padrão do projeto.</p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 flex flex-col">
              <p className="font-mono text-[10px] tracking-widest text-gold">PASSO 1</p>
              <h2 className="mt-2 font-display font-bold">Baixar o template</h2>
              <p className="mt-1 text-sm text-zinc-400 flex-1">Planilha Excel com as colunas certas, exemplo preenchido e aba de instruções (perfis, faixas e unidades válidas).</p>
              <button type="button" onClick={downloadTemplate} data-testid="carga-template-button"
                className="mt-4 inline-flex items-center justify-center gap-2 font-display font-semibold border border-gold/50 text-gold px-6 py-3 rounded-full hover:bg-gold/10 transition-colors">
                <Download size={16} /> Baixar Template (.xlsx)
              </button>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 flex flex-col">
              <p className="font-mono text-[10px] tracking-widest text-gold">PASSO 2</p>
              <h2 className="mt-2 font-display font-bold">Enviar a planilha preenchida</h2>
              <p className="mt-1 text-sm text-zinc-400 flex-1">O sistema valida tudo antes de importar: dados obrigatórios, menores sem responsável e e-mails já cadastrados.</p>
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="carga-file-input"
                onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ""; }} />
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="carga-upload-button"
                className="mt-4 inline-flex items-center justify-center gap-2 font-display font-semibold bg-gold text-obsidian px-6 py-3 rounded-full hover:bg-gold-amber transition-colors">
                <Upload size={16} /> {file ? "Trocar Arquivo" : "Selecionar Arquivo"}
              </button>
            </div>
          </div>

          {file && !result && (
            <div className="mt-6 rounded-2xl border border-gold/30 bg-gold/[0.04] p-5" data-testid="carga-file-ready">
              <p className="text-sm flex items-center gap-2"><FileSpreadsheet size={16} className="text-gold" /> <strong>{file.name}</strong></p>
              {!preview && (
                <button type="button" onClick={validar} disabled={busy} data-testid="carga-validate-button"
                  className="mt-4 inline-flex items-center gap-2 font-display font-semibold bg-gold text-obsidian px-7 py-3 rounded-full hover:bg-gold-amber transition-colors disabled:opacity-60">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Validar Planilha
                </button>
              )}
            </div>
          )}

          {preview && (
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="carga-preview">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-xl border border-white/10 p-3">
                  <p className="font-display text-2xl font-bold">{preview.total_linhas}</p>
                  <p className="font-mono text-[9px] tracking-widest text-zinc-500">LINHAS</p>
                </div>
                <div className="rounded-xl border border-emerald-500/40 p-3">
                  <p className="font-display text-2xl font-bold text-emerald-400" data-testid="carga-validos">{preview.validos}</p>
                  <p className="font-mono text-[9px] tracking-widest text-zinc-500">VÁLIDAS</p>
                </div>
                <div className="rounded-xl border border-crimson/40 p-3">
                  <p className="font-display text-2xl font-bold text-crimson" data-testid="carga-erros-count">{preview.erros.length}</p>
                  <p className="font-mono text-[9px] tracking-widest text-zinc-500">COM ERRO</p>
                </div>
              </div>

              {preview.erros.length > 0 && <ErrosTable erros={preview.erros} testid="carga-erros" />}

              {preview.validos > 0 && (
                <div className="mt-5 rounded-xl border border-gold/40 bg-gold/[0.06] p-4" data-testid="carga-confirm-box">
                  <p className="text-sm text-zinc-200 flex items-start gap-2">
                    <Mail size={16} className="text-gold shrink-0 mt-0.5" />
                    <span>
                      Ao confirmar, <strong>{preview.validos} usuário(s)</strong> serão cadastrados
                      {preview.menores_com_termo > 0
                        ? <> e <strong>{preview.menores_com_termo} e-mail(s) de Termo de Responsabilidade</strong> serão enviados aos responsáveis legais dos menores para assinatura digital.</>
                        : ". Nenhum menor com responsável nesta carga — nenhum e-mail de termo será enviado."}
                    </span>
                  </p>
                  <div className="mt-4 flex gap-3 flex-wrap">
                    <button type="button" onClick={confirmar} disabled={busy} data-testid="carga-confirm-button"
                      className="inline-flex items-center gap-2 font-display font-semibold bg-gold text-obsidian px-7 py-3 rounded-full hover:bg-gold-amber transition-colors disabled:opacity-60">
                      {busy ? <Loader2 size={16} className="animate-spin" /> : <Users size={16} />}
                      Confirmar e Importar {preview.validos} Usuário(s)
                    </button>
                    <button type="button" onClick={() => setPreview(null)} data-testid="carga-cancel-button"
                      className="font-mono text-xs tracking-widest border border-white/15 text-zinc-300 px-5 py-3 rounded-full hover:border-crimson/60 hover:text-crimson transition-colors">
                      CANCELAR
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {result && (
            <div className="mt-6 rounded-2xl border border-emerald-500/40 bg-emerald-500/[0.05] p-5" data-testid="carga-result">
              <p className="font-display font-bold text-emerald-400 flex items-center gap-2"><CheckCircle2 size={18} /> Importação concluída</p>
              <p className="mt-2 text-sm text-zinc-300" data-testid="carga-result-summary">
                <strong>{result.criados}</strong> usuário(s) cadastrados · <strong>{result.termos_enviados}</strong> e-mail(s) de termo enviados aos responsáveis.
              </p>
              {result.erros.length > 0 && <ErrosTable erros={result.erros} testid="carga-result-erros" />}
              <button type="button" onClick={() => { setFile(null); setResult(null); }} data-testid="carga-reset-button"
                className="mt-4 font-mono text-xs tracking-widest border border-white/15 text-zinc-300 px-5 py-2.5 rounded-full hover:border-gold/50 hover:text-gold transition-colors">
                NOVA CARGA
              </button>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
